import React, {useEffect, useState} from "react";
import {Drawer} from "vaul";
import {Button} from "../../components/ui/button";
import {Input} from "../../components/ui/input";
import {Textarea} from "../../components/ui/textarea";
import {Checkbox} from "../../components/ui/checkbox";
import {PencilIcon, PlusIcon, SaveIcon, SearchIcon, TrashIcon} from "lucide-react";
import axios from "../../utility/customAxios";
import {useSearchParams} from "react-router-dom";
import {LoadingSpinner} from "../../components/ui/customSpinner";
import {LogSearch} from "../../pages/Logs";
import {MetoroQLQuerySpecifier, MetricSpecifier} from "../../clients/metoro/metrics";
import {dashboardJsonReplacer, dashboardJsonReviver} from "../Dashboarding/Dashboard";
import {Transform} from "../Dashboarding/widgets/TransformSelector";

export interface SavedSearch {
    uuid: string;
    name: string;
    description: string;
    query: string;
    type: string;
    userId: string;
    isPersonal: boolean;
    createdAt: number;
    updatedAt: number;
}

interface SavedSearchesDrawerProps {
    metricSpecifier?: MetricSpecifier,
    metoroQLQuerySpecifier?: MetoroQLQuerySpecifier
    setIsMetoroQLMode?: (isMetoroQLMode: boolean) => void;
    setMetoroQLQuery?: (query: string) => void;
    setLocalMetoroQLQuery?: (query: string) => void;
    setFilter: (filter: Map<string, string[]>) => void;
    setExcludeFilter: (filter: Map<string, string[]>) => void;
    setRegexes: (regexes: string[]) => void;
    setExcludeRegexes: (regexes: string[]) => void;
    setTransform: (transform: Transform[]) => void;
}

export function SavedLogSearchesDrawer(props: SavedSearchesDrawerProps) {
    const [open, setOpen] = useState(false);
    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [createMode, setCreateMode] = useState(false);
    const [currentSearch, setCurrentSearch] = useState<SavedSearch | null>(null);
    const [searchName, setSearchName] = useState("");
    const [searchDescription, setSearchDescription] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isPersonal, setIsPersonal] = useState(true);
    const [, setSearchParams] = useSearchParams();

    // Fetch saved searches when the drawer is opened
    useEffect(() => {
        if (open) {
            fetchSavedSearches();
        } else {
            // Clear search term when drawer is closed
            setSearchTerm("");
        }
    }, [open]);

    const fetchSavedSearches = async () => {
        setLoading(true);
        try {
            const response = await axios.get("/api/v1/saved_searches");
            setSavedSearches(response.data.savedSearches || []);
        } catch (error) {
            console.error("Error fetching saved searches:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCurrentSearch = () => {
        setCreateMode(true);
        setEditMode(false);
        setCurrentSearch(null);
        setSearchName("");
        setSearchDescription("");
        setSearchTerm("");
        setIsPersonal(true);
    };

    const handleEditSearch = (search: SavedSearch) => {
        setEditMode(true);
        setCreateMode(false);
        setCurrentSearch(search);
        setSearchName(search.name);
        setSearchDescription(search.description);
        setIsPersonal(search.isPersonal);
        setSearchTerm("");
    };

    const handleDeleteSearch = async (uuid: string) => {
        try {
            await axios.delete(`/api/v1/saved_search?uuid=${uuid}`);
            fetchSavedSearches();
        } catch (error) {
            console.error("Error deleting saved search:", error);
        }
    };

    const handleSaveSearch = async () => {
        if (!searchName.trim()) {
            alert("Please enter a name for the saved search");
            return;
        }

        try {
            const query = JSON.stringify({
                metricSpecifier: props.metricSpecifier,
                metoroQLQuerySpecifier: props.metoroQLQuerySpecifier,
            }, dashboardJsonReplacer);

            const savedSearch: SavedSearch = {
                uuid: currentSearch?.uuid || "",
                name: searchName,
                description: searchDescription,
                query: query,
                type: "logs",
                userId: "",
                isPersonal: isPersonal,
                createdAt: 0,
                updatedAt: 0
            };

            await axios.post("/api/v1/saved_search", {
                savedSearch
            });

            setCreateMode(false);
            setEditMode(false);
            setCurrentSearch(null);
            setSearchName("");
            setSearchDescription("");
            setSearchTerm("");
            setIsPersonal(true);
            fetchSavedSearches();
        } catch (error) {
            console.error("Error saving search:", error);
        }
    };

    const handleLoadSearch = (search: SavedSearch) => {
        try {
            const queryData = JSON.parse(search.query, dashboardJsonReviver);

            // Handle MetoroQL mode
            if (queryData.metoroQLQuerySpecifier !== undefined) {
                if (props.setIsMetoroQLMode) {
                    props.setIsMetoroQLMode(true);
                }
                if (props.setMetoroQLQuery) {
                    props.setMetoroQLQuery(queryData.metoroQLQuerySpecifier.query);
                }
                if (props.setLocalMetoroQLQuery) {
                    props.setLocalMetoroQLQuery(queryData.metoroQLQuerySpecifier.query);
                }
            } else {
                console.log(queryData);
                if (props.setIsMetoroQLMode) {
                    props.setIsMetoroQLMode(false);
                }
                if (props.setMetoroQLQuery) {
                    props.setMetoroQLQuery("");
                }
                props.setFilter(new Map(queryData.metricSpecifier.filters));
                props.setExcludeFilter(new Map(queryData.metricSpecifier.excludeFilters));
                props.setRegexes(queryData.metricSpecifier.regexes || []);
                props.setExcludeRegexes(queryData.metricSpecifier.excludeRegexes || []);
                props.setTransform(queryData.metricSpecifier.transforms || []);
            }

            setOpen(false);
        } catch (error) {
            console.error("Error loading saved search:", error);
        }
    };

    const cancelEdit = () => {
        setCreateMode(false);
        setEditMode(false);
        setCurrentSearch(null);
        setSearchName("");
        setSearchDescription("");
        setSearchTerm("");
        setIsPersonal(true);
    };

    return (
        <Drawer.Root modal={false} direction="right" open={open} onOpenChange={setOpen}>
            <Drawer.Trigger asChild>
                <div
                    className="hover:cursor-pointer h-full px-1 flex flex-column items-center justify-center text-textmedium hover:bg-backgroundlight">
                    <SaveIcon className="h-4 w-4 mx-[2px]"/>
                </div>
            </Drawer.Trigger>
            <Drawer.Portal>
                <Drawer.Content
                    data-vaul-no-drag
                    draggable={false}
                    onInteractOutside={() => setOpen(false)}
                    className="select-text flex border-l flex-col h-full w-2/3 max-w-[800px] mt-24 fixed bottom-0 right-0 bg-backgroundmedium text-textmedium z-[200]">
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <Drawer.Title className="text-lg font-medium text-textmedium h-[32px]">
                                Saved Searches
                            </Drawer.Title>
                            {createMode || editMode ? null :
                                <Button className={"border border-primary bg-primarytransparent h-[32px]"}
                                        onClick={handleSaveCurrentSearch} variant="outline" size="sm">
                                    <PlusIcon className="h-4 w-4 mr-2"/>
                                    Save Current
                                </Button>}
                        </div>
                        {!createMode && !editMode && savedSearches.length > 0 && (
                            <div className="mt-4 px-4 relative">
                                <SearchIcon
                                    className="h-4 w-4 mx-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-textdark"/>
                                <Input
                                    placeholder="Search saved searches..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 w-full h-8"
                                />
                            </div>
                        )}
                    </div>

                    <div className="p-4 mt-4 pt-0 flex-1 overflow-auto">
                        {loading ? (
                            <div className="flex justify-center items-center h-full">
                                <LoadingSpinner/>
                            </div>
                        ) : createMode || editMode ? (
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-textmedium">
                                    {createMode ? "Create Saved Search" : "Edit Saved Search"}
                                </h3>
                                <div className="space-y-2">
                                    <label htmlFor="name" className="text-sm font-medium text-textmedium">
                                        Name
                                    </label>
                                    <Input
                                        id="name"
                                        value={searchName}
                                        onChange={(e) => setSearchName(e.target.value)}
                                        placeholder="Enter a name for this search"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="description"
                                           className="text-sm font-medium text-textmedium bg-backgrounddark">
                                        Description
                                    </label>
                                    <Textarea
                                        id="description"
                                        value={searchDescription}
                                        className="font-mono text-sm bg-backgrounddark border border-border text-textmedium p-4 overflow-y-auto rounded"
                                        onChange={(e) => setSearchDescription(e.target.value)}
                                        placeholder="Enter a description (optional)"
                                        rows={3}
                                    />
                                </div>

                                {/* Add LogSearch component to show current filters */}
                                <div className="space-y-2 mt-4">
                                    <label className="text-sm font-medium text-textmedium">
                                        Current Filters
                                    </label>
                                    <div className="border border-border rounded bg-backgrounddark overflow-hidden">
                                        <LogSearch
                                            filter={props.metricSpecifier?.filters ? props.metricSpecifier.filters : new Map()}
                                            excludeFilter={props.metricSpecifier?.excludeFilters ? props.metricSpecifier.excludeFilters : new Map()}
                                            regexes={props.metricSpecifier?.regexes ? props.metricSpecifier.regexes : []}
                                            excludeRegexes={props.metricSpecifier?.excludeRegexes ? props.metricSpecifier.excludeRegexes : []}
                                            transforms={props.metricSpecifier?.transforms ? props.metricSpecifier.transforms : []}
                                            // Dummy setter functions to make it read-only
                                            setFilter={() => {
                                            }}
                                            setExcludeFilter={() => {
                                            }}
                                            setRegexes={() => {
                                            }}
                                            setExcludeRegexes={() => {
                                            }}
                                            setFreeTextFilter={() => {
                                            }}
                                            setTransforms={() => {}}
                                            // MetoroQL related props
                                            isMetoroQLMode={!!props.metoroQLQuerySpecifier}
                                            metoroQLQuery={props.metoroQLQuerySpecifier ? props.metoroQLQuerySpecifier.query : undefined}
                                            setIsMetoroQLMode={() => {
                                            }}
                                            setMetoroQLQuery={() => {
                                            }}
                                            embeded={true}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2 pt-2">
                                    <Checkbox
                                        id="isPersonal"
                                        checked={isPersonal}
                                        onCheckedChange={(checked) => setIsPersonal(checked === true)}
                                    />
                                    <label
                                        htmlFor="isPersonal"
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-textmedium cursor-pointer"
                                    >
                                        Make this search personal (only visible to you)
                                    </label>
                                </div>
                                <div className="flex justify-end space-x-2 pt-2">
                                    <Button className={"bg-redtransparenter border border-red-500"}
                                            onClick={cancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button className={"bg-primarytransparent border border-primary"}
                                            onClick={handleSaveSearch}>Save</Button>
                                </div>
                            </div>
                        ) : savedSearches.length === 0 ? (
                            <div className="flex justify-center items-center h-full">
                                <div className="text-center py-8 text-textdark">
                                    No saved searches found. Create one by clicking "Save Current".
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {searchTerm.trim() !== "" &&
                                    savedSearches.filter(search =>
                                        search.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        search.description.toLowerCase().includes(searchTerm.toLowerCase())
                                    ).length === 0 && (
                                        <div className="flex justify-center items-center h-32">
                                            <div className="text-center text-textdark">
                                                No results found for "{searchTerm}".
                                            </div>
                                        </div>
                                    )
                                }
                                {(searchTerm.trim() === "" ? savedSearches : savedSearches.filter(search =>
                                    search.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    search.description.toLowerCase().includes(searchTerm.toLowerCase())
                                )).map((search) => (
                                    <div
                                        key={search.uuid}
                                        className="border rounded p-3 hover:border-primary hover:bg-backgroundlight cursor-pointer flex justify-between items-start transition-colors"
                                        onClick={() => handleLoadSearch(search)}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center">
                                                <h3 className="font-medium text-textmedium">{search.name}</h3>
                                                {search.isPersonal && (
                                                    <span
                                                        className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded-full">
                            Personal
                          </span>
                                                )}
                                            </div>
                                            {search.description && (
                                                <p className="text-sm text-textmedium mt-1">
                                                    {search.description}
                                                </p>
                                            )}
                                            <p className="text-xs text-textdark mt-2">
                                                Last updated: {new Date(search.updatedAt * 1000).toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="flex space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditSearch(search);
                                                }}
                                            >
                                                <PencilIcon className="h-4 w-4"/>
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 hover:text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteSearch(search.uuid);
                                                }}
                                            >
                                                <TrashIcon className="h-4 w-4"/>
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    )
        ;
}
